import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const DischargeDetails = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
const [dischargeFiles, setDischargeFiles] = useState([]);

  const formatForInput = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toISOString().slice(0, 16);
  };

  const initialFormState = {
    discharge_id: "",
    admission_id: "",
    patient_id: "",
    patient_name: "", // UI only
    discharge_date: formatForInput(new Date()),
    discharge_summary: "",
    final_diagnosis: "",
    condition_at_discharge: "Stable",
    follow_up_instructions: "",
    prescriptions_at_discharge: "",
    upload_file_name: "", // File name reference
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- SEARCH LOGIC ---
  const handleSearch = async (text) => {
    setSearchText(text);
    if (text.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      // Pass the screenAction so the API knows what to search for (Active Admissions vs Existing Discharges)
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/discharge/search?q=${text}&mode=${screenAction}&employee_id=${user?.id}`);
      const data = await res.json();
      setSearchResults(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };




  const selectRecord = (record) => {
if (screenAction === "add") {
  setFormData({
    ...initialFormState,
    admission_id: record.admission_id,
    patient_id: record.patient_id,
    patient_name: record.patient_name,
    _isExisting: false
  });

  setSearchText(`${record.admission_id} | ${record.patient_name}`);
} else {
  // existing discharge
  setFormData({
    ...record,
    discharge_date: formatForInput(record.discharge_date),
    _isExisting: true
  });

  setSearchText(`Discharge: ${record.discharge_id} | Adm: ${record.admission_id}`);

  if (record.upload_file_name) {
    const fileList = JSON.parse(record.upload_file_name);

    const formatted = fileList.map(fileName => ({
      file: null,
      preview: fileName,
      isExisting: true,
      fileName: fileName
    }));

    setDischargeFiles(formatted);
  }
}
    
    setShowDropdown(false);
    setIsDisabled(screenAction === "view");
  };

  // --- FORM ACTIONS ---
  const clearForm = () => {
    setFormData(initialFormState);
    setSearchText("");
    setDischargeFiles([]);
    setIsDisabled(screenAction === "view");
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    clearForm();
    if (screenAction === "add" || screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleFileUpload = (e) => {
  const files = Array.from(e.target.files);

  const filesWithPreview = files.map(file => ({
    file,
    preview: URL.createObjectURL(file),
    isExisting: false
  }));

  setDischargeFiles(prev => [...prev, ...filesWithPreview]);
};

const removeFile = (index) => {
  const updated = [...dischargeFiles];
  updated.splice(index, 1);
  setDischargeFiles(updated);
};

  // --- SAVE LOGIC ---
  const handleSubmit = async () => {
    if (!formData.admission_id || !formData.patient_id) {
      return alert("Please search and select a valid Admission/Patient first!");
    }

  const formDataObj = new FormData();
const user = JSON.parse(localStorage.getItem("user"));

const payload = { ...formData, created_by: user?.id };
delete payload.patient_name;
delete payload._isExisting;
delete payload.status;
delete payload.discharge_id;
// append text fields
Object.keys(payload).forEach(key => {
  if (payload[key] !== undefined && payload[key] !== null) {
    formDataObj.append(key, payload[key]);
  }
});

// existing files (if editing)
const existingFiles = dischargeFiles
  .filter(f => f.isExisting)
  .map(f => f.fileName);

formDataObj.append("existing_files", JSON.stringify(existingFiles));

// new files
dischargeFiles.forEach(item => {
  if (!item.isExisting) {
    formDataObj.append("discharge_files", item.file);
  }
});
    
 const isEdit = formData._isExisting && formData.discharge_id;

const method = isEdit ? "PUT" : "POST";

const url = isEdit
  ? `https://hmsapi.emedhatechnologies.com/master/api/api/discharge/${formData.discharge_id}`
  : "https://hmsapi.emedhatechnologies.com/master/api/api/discharge";
    try {
      const res = await fetch(url, {
       method,
  body: formDataObj
      });
      
      if (res.ok) {
        alert(method === "POST" ? "Patient Discharged Successfully!" : "Discharge Record Updated!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving record.");
      }
    } catch (err) {
      alert("Network error.");
    }
  };


  useEffect(() => {
  return () => {
    dischargeFiles.forEach(file => {
      if (file.preview && file.preview.startsWith("blob:")) {
        URL.revokeObjectURL(file.preview);
      }
    });
  };
}, [dischargeFiles]);

  // --- PRINT FORM ---
  const printForm = () => {
    const newWindow = window.open("", "", "width=900,height=800");
    const today = new Date().toLocaleDateString();
    
    const htmlContent = `
      <html>
        <head>
          <title>Discharge Summary - ${formData.admission_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #27ae60; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #27ae60; font-size: 18px; }
            .section-header { background: #eafaf1; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #27ae60; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
            .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 180px; color: #555; }
            .value { flex: 1; color: #000; font-weight: 500; }
            .full-width { grid-column: span 2; }
            .text-box { font-size: 13px; padding: 10px; border: 1px solid #ddd; background: #fafafa; min-height: 60px; white-space: pre-wrap; }
            .sig-box { margin-top: 60px; display: flex; justify-content: space-between; }
            .sig { border-top: 1px solid #000; width: 200px; text-align: center; padding-top: 5px; font-size: 12px; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Inpatient Services</p>
            </div>
            <div class="report-title">
              <h2>DISCHARGE SUMMARY</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Patient & Admission Info</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Patient Name:</span> <span class="value" style="font-weight:bold;">${formData.patient_name || 'N/A'}</span></div>
            <div class="data-item"><span class="label">Patient ID:</span> <span class="value">${formData.patient_id}</span></div>
            <div class="data-item"><span class="label">Admission ID:</span> <span class="value">${formData.admission_id}</span></div>
            <div class="data-item"><span class="label">Discharge Date/Time:</span> <span class="value">${new Date(formData.discharge_date).toLocaleString()}</span></div>
            <div class="data-item"><span class="label">Discharge ID:</span> <span class="value">${formData.discharge_id || 'Pending Generation'}</span></div>
            <div class="data-item"><span class="label">Condition at Discharge:</span> <span class="value">${formData.condition_at_discharge}</span></div>
          </div>

          <div class="section-header">Final Diagnosis</div>
          <div class="text-box">${formData.final_diagnosis || 'N/A'}</div>

          <div class="section-header">Discharge Summary</div>
          <div class="text-box">${formData.discharge_summary || 'N/A'}</div>

          <div class="section-header">Follow-up Instructions</div>
          <div class="text-box">${formData.follow_up_instructions || 'N/A'}</div>

          <div class="section-header">Prescriptions at Discharge</div>
          <div class="text-box">${formData.prescriptions_at_discharge || 'N/A'}</div>

          <div class="sig-box">
             <div class="sig">Patient / Relatives Signature</div>
             <div class="sig">Consultant Signature</div>
          </div>

          <div class="footer">
            System Generated Report - Emedha HMS | Wishing you a speedy recovery.
          </div>
        </body>
      </html>
    `;
    
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      
      {/* Dynamic Search Bar based on Action */}
      {(screenAction === "add" || screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>
            {screenAction === "add" ? "Search Active Admissions (To Discharge)" : "Search Existing Discharges"}
          </label>
          <input 
            type="text" 
            value={searchText} 
            placeholder="Search by ID, Admission ID, or Patient Name..." 
            onChange={(e) => handleSearch(e.target.value)} 
            autoFocus 
          />
          {showDropdown && searchResults.length > 0 && (
            <div className="patient-dropdown">
              {searchResults.map((res, index) => (
                <div key={index} className="patient-item" onClick={() => selectRecord(res)}>
                  {screenAction === "add" 
                    ? `Adm: ${res.admission_id} | ${res.patient_name} (${res.patient_id})` 
                    : `Discharge: ${res.discharge_id} | Adm: ${res.admission_id} | ${res.patient_name}`
                  }
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* Identification */}
        <div className="form-section-header"><h3>Identification Details</h3></div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Discharge ID</label>
            <input type="text" value={formData.discharge_id} readOnly placeholder={screenAction === "add" ? "Auto Generated" : ""} />
          </div>
          <div className="input-box read-only">
            <label>Admission ID <span style={{color:'red'}}>*</span></label>
            <input type="text" value={formData.admission_id} readOnly placeholder="Search & Select Above" />
          </div>
          <div className="input-box read-only">
            <label>Patient ID <span style={{color:'red'}}>*</span></label>
            <input type="text" value={formData.patient_id} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Patient Name</label>
            <input type="text" value={formData.patient_name} readOnly />
          </div>
          <div className="input-box">
            <label>Discharge Date & Time <span style={{color:'red'}}>*</span></label>
            <input type="datetime-local" name="discharge_date" value={formData.discharge_date} onChange={handleChange} disabled={isDisabled || !formData.admission_id} />
          </div>
          <div className="input-box">
            <label>Condition at Discharge</label>
            <select name="condition_at_discharge" value={formData.condition_at_discharge} onChange={handleChange} disabled={isDisabled || !formData.admission_id}>
              <option value="Stable">Stable</option>
              <option value="Recovered">Recovered</option>
              <option value="Referred to another Hospital">Referred to another Hospital</option>
              <option value="Absconded">Absconded</option>
              <option value="Deceased">Deceased</option>
            </select>
          </div>
        </div>

        {/* Clinical Summaries */}
        <div className="form-section-header"><h3>Clinical Summaries (Remarks)</h3></div>
        <div className="form-grid">
          <div className="input-box">
            <label>Final Diagnosis</label>
            <textarea name="final_diagnosis" value={formData.final_diagnosis} onChange={handleChange} disabled={isDisabled || !formData.admission_id} rows="3" placeholder="Enter final confirmed diagnosis..."></textarea>
          </div>
          <div className="input-box">
            <label>Discharge Summary</label>
            <textarea name="discharge_summary" value={formData.discharge_summary} onChange={handleChange} disabled={isDisabled || !formData.admission_id} rows="3" placeholder="Enter comprehensive summary of stay..."></textarea>
          </div>
        </div>

        {/* Instructions & Prescriptions */}
        <div className="form-section-header"><h3>Instructions & Files</h3></div>
        <div className="form-grid">
          <div className="input-box ">
            <label>Follow-up Instructions</label>
            <textarea name="follow_up_instructions" value={formData.follow_up_instructions} onChange={handleChange} disabled={isDisabled || !formData.admission_id} rows="3" placeholder="Diet, rest, and next visit dates..."></textarea>
          </div>
          <div className="input-box">
            <label>Prescriptions at Discharge</label>
            <textarea name="prescriptions_at_discharge" value={formData.prescriptions_at_discharge} onChange={handleChange} disabled={isDisabled || !formData.admission_id} rows="3" placeholder="List medications to take home..."></textarea>
          </div>
          <div className="input-box" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', border: '1px dashed #ccc' }}>
            <label style={{ display: 'block', marginBottom: '10px' }}>Upload Additional Documents (Scans, Lab Reports)</label>
         <input 
  type="file" 
  multiple
  onChange={handleFileUpload} 
  disabled={isDisabled || !formData.admission_id} 
/>
{formData.upload_file_name && (() => {
  try {
    const files = JSON.parse(formData.upload_file_name);

    const fileNames = files.map(url => url.split('/').pop());

    return (
      <p style={{ fontSize: '12px', color: 'green', marginTop: '10px' }}>
        ✓ Attached: {fileNames.join(', ')}
      </p>
    );
  } catch {
    return null;
  }
})()}
          </div>
<div className="preview-container">
  {dischargeFiles.map((item, index) => (
    <div key={index} className="preview-box">
      <img 
        src={item.preview} 
        alt="file" 
        style={{ width: 100, height: 100, borderRadius: 8 }} 
      />
      {!isDisabled && (
        <button 
          type="button" 
          className="remove-btn" 
          onClick={() => removeFile(index)}
        >
          ✕
        </button>
      )}
    </div>
  ))}
</div>
        </div>

      </form>
    </div>
  );
};

export default DischargeDetails;